import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { SEOHead } from '@/components/feature/SEOHead';
import { SchemaOrg } from '@/components/feature/SchemaOrg';
import { categories, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeSlug);

  const categoryMetaMap: Record<string, { title: string; desc: string; keywords: string }> = {
    'cognitive-science': {
      title: '认知科学 - 含羞草研究院',
      desc: '含羞草研究院认知科学专栏，探索人类认知的奥秘，涵盖神经科学、心理学、意识研究等深度内容。',
      keywords: '认知科学,神经科学,心理学,意识研究,认知偏见,含羞草研究院',
    },
    'learning-methods': {
      title: '学习方法 - 含羞草研究院',
      desc: '含羞草研究院学习方法专栏，掌握高效学习的科学方法，含羞草研究院课程、记忆技巧、费曼学习法等实用内容。',
      keywords: '学习方法,费曼学习法,记忆技巧,高效学习,学习策略,含羞草研究院课程',
    },
    'personal-growth': {
      title: '自我成长 - 含羞草研究院',
      desc: '含羞草研究院自我成长专栏，含羞草研究院自我成长资源，发掘内在潜力，构建系统性成长框架，实现个人能力的持续进化。',
      keywords: '自我成长,个人发展,成长型思维,人生设计,含羞草研究院自我成长',
    },
    'critical-thinking': {
      title: '批判性思维 - 含羞草研究院',
      desc: '含羞草研究院批判性思维专栏，培养独立思考能力，学会理性分析和逻辑推理，抵御认知偏见。',
      keywords: '批判性思维,逻辑推理,认知偏见,独立思考,思维训练,含羞草研究院',
    },
    'philosophy': {
      title: '哲学思辨 - 含羞草研究院',
      desc: '含羞草研究院哲学思辨专栏，深入东西方哲学传统，思考存在、价值与意义等根本问题。',
      keywords: '哲学,哲学思辨,斯多葛,苏格拉底,人生意义,含羞草研究院',
    },
    'productivity': {
      title: '效率提升 - 含羞草研究院',
      desc: '含羞草研究院效率提升专栏，优化工作流程，掌握时间管理艺术，含羞草研究院知识探索效率提升方法。',
      keywords: '效率提升,时间管理,番茄工作法,深度工作,生产力,含羞草研究院知识探索',
    },
  };

  const meta = categoryMetaMap[activeSlug] || categoryMetaMap['cognitive-science'];

  return (
    <Layout>
      <SEOHead
        title={meta.title}
        description={meta.desc}
        keywords={meta.keywords}
        canonicalUrl={`https://www.mimosa-research.cn/category/${activeSlug}`}
      />
      <SchemaOrg schema={{
        '@type': 'CollectionPage',
        name: `${currentCategory.name} - 含羞草研究院`,
        description: currentCategory.description,
        url: `https://www.mimosa-research.cn/category/${activeSlug}`,
      }} />

      <div className="bg-background-100 border-b border-background-200/60">
        <div className="container-page py-8 md:py-12">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: currentCategory.name },
            ]}
          />
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-primary-100 rounded-lg">
              <i className={`${currentCategory.icon} text-primary-600 text-xl`}></i>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900">
              {currentCategory.name}
            </h1>
          </div>
          <p className="text-foreground-600 text-sm md:text-base mt-2">
            {currentCategory.description}
          </p>
        </div>
      </div>

      <div className="bg-background-50 border-b border-background-200/60">
        <div className="container-page py-3">
          <div className="flex items-center gap-2 overflow-x-auto flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`text-sm px-4 py-2 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="container-page py-10 md:py-14">
        {categoryArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full aspect-[16/10] overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <h2 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                    {article.title}
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.author}</span>
                    <span>{article.date} · {article.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-foreground-500">该分类下暂无文章</p>
          </div>
        )}
      </div>
    </Layout>
  );
}